import { useCallback, useRef } from 'react';

/**
 * Returns a debounced version of the callback that ignores calls made within the delay period.
 * Unlike useDebounce (which debounces values), this executes immediately on first call
 * and ignores subsequent calls until the delay has passed.
 *
 * @param callback - The function to debounce
 * @param delay - Minimum time between calls in milliseconds (default: 1000ms)
 * @returns A debounced version of the callback
 */
export const useDebouncedCallback = <T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay = 1000
): ((...args: Parameters<T>) => void) => {
  const lastCallRef = useRef<number>(0);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastCallRef.current < delay) {
        return;
      }
      lastCallRef.current = now;
      callback(...args);
    },
    [callback, delay]
  );
};
